import { isInternalEventLink } from "@/lib/utils/linkUtils";
import { shouldShowPreview } from "@/lib/utils/urlUtils";

export type StreamPreviewItem =
  | { type: "post"; url: string; studyGroupId: string; postId: string }
  | { type: "studyGroup"; url: string; studyGroupId: string }
  | { type: "chat"; url: string; chatRoomId: string }
  | {
      type: "files";
      url: string;
      studyGroupId: string;
      folderId?: string;
      fileId?: string;
    }
  | { type: "generic"; url: string };

const normalizeUrl = (url: string): URL | null => {
  try {
    const base =
      typeof window === "undefined"
        ? "http://localhost"
        : window.location.origin;
    return new URL(url, base);
  } catch {
    return null;
  }
};

const isRelativeUrl = (url: string): boolean =>
  url.startsWith("/") && !url.startsWith("//");

const isCurrentOriginUrl = (parsedUrl: URL): boolean => {
  if (typeof window === "undefined") {
    return parsedUrl.origin === "http://localhost";
  }

  return parsedUrl.origin === window.location.origin;
};

export const buildStreamPreviewItem = (
  url: string,
): StreamPreviewItem | null => {
  const parsedUrl = normalizeUrl(url);
  if (!parsedUrl) {
    return null;
  }

  if (isInternalEventLink(parsedUrl.toString())) {
    return null;
  }

  const pathParts = parsedUrl.pathname.split("/").filter(Boolean);
  const [firstSegment, firstResourceId, resourceSegment, resourceId] =
    pathParts;
  const canUseRuleWatcherPreview =
    isRelativeUrl(url) || isCurrentOriginUrl(parsedUrl);

  if (
    firstSegment === "chat-room" &&
    firstResourceId &&
    pathParts.length === 2
  ) {
    if (!canUseRuleWatcherPreview) {
      if (!shouldShowPreview(url)) {
        return null;
      }

      return { type: "generic", url };
    }

    return {
      type: "chat",
      url,
      chatRoomId: firstResourceId,
    };
  }

  if (
    firstSegment === "all-study-groups" &&
    firstResourceId &&
    pathParts.length === 2
  ) {
    if (!canUseRuleWatcherPreview) {
      if (!shouldShowPreview(url)) {
        return null;
      }

      return { type: "generic", url };
    }

    return {
      type: "studyGroup",
      url,
      studyGroupId: firstResourceId,
    };
  }

  if (firstSegment !== "study-group" || !firstResourceId) {
    if (!shouldShowPreview(url)) {
      return null;
    }

    return { type: "generic", url };
  }

  if (!canUseRuleWatcherPreview) {
    if (!shouldShowPreview(url)) {
      return null;
    }

    return { type: "generic", url };
  }

  if (resourceSegment === "post" && resourceId) {
    return {
      type: "post",
      url,
      studyGroupId: firstResourceId,
      postId: resourceId,
    };
  }

  if (resourceSegment === "files" && pathParts.length === 3) {
    const fileId = parsedUrl.searchParams.get("file_id") ?? undefined;

    if (fileId) {
      return {
        type: "files",
        url,
        studyGroupId: firstResourceId,
        fileId,
      };
    }
  }

  if (resourceSegment === "files" && pathParts[3] === "folder") {
    const fileId = parsedUrl.searchParams.get("file_id") ?? undefined;
    const folderId = pathParts[4];

    if (!folderId) {
      return null;
    }

    return {
      type: "files",
      url,
      studyGroupId: firstResourceId,
      folderId: pathParts[4],
      fileId,
    };
  }

  if (!shouldShowPreview(url)) {
    return null;
  }

  return { type: "generic", url };
};

export const findFirstStreamPreviewItem = (
  urls: string[],
): StreamPreviewItem | null => {
  for (const url of urls) {
    const item = buildStreamPreviewItem(url);
    if (item) return item;
  }

  return null;
};
